import React, { useState } from "react";
import useLevelBookingWarning from "hooks/useLevelBookingWarning";
import { useMutation } from "@apollo/client";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// graphql
import GET_CURRENT_USER_INFO from "graphql/queries/GetCurrentUserInfo";
import GET_NAVBAR_INFO from "graphql/queries/GetNavbarInfo";
import GET_CLASS_DETAILS from "graphql/queries/GetClassDetails";
import GET_MY_CLASSES from "graphql/queries/GetMyClasses";
import BOOK_GROUP_CLASS from "graphql/mutations/BookGroupClass";

// common
import { ModalText, LargeModalStyling, MoreModalButtons } from "common/modal";
import BookingActions from "./GroupModalStyle";

const { LargeModal, LargeModalCreditInfo } = LargeModalStyling;
const { ConfirmationText, CreditWarning } = ModalText;
const { ModalButtonYellow, ModalButtonBlue } = MoreModalButtons;

type GroupModalProps = {
  visible: boolean;
  onCloseModal: () => void;
  credits: number;
  classCredit: number;
  state: { classId: number };
  handleBookingError: (error: any) => void;
  handleBookingSuccess: () => void;
};

const GroupModal = ({
  visible,
  onCloseModal,
  credits,
  classCredit,
  state,
  handleBookingError,
  handleBookingSuccess,
}: GroupModalProps) => {
  const { t } = useTranslation("book_a_class");

  const { confirmLevel } = useLevelBookingWarning();
  const [submitting, setSubmitting] = useState(false);

  const [bookGroup] = useMutation(BOOK_GROUP_CLASS, {
    refetchQueries: [
      {
        query: GET_CLASS_DETAILS,
        variables: { id: state.classId },
      },
      {
        query: GET_MY_CLASSES,
      },
      {
        query: GET_CURRENT_USER_INFO,
      },
      {
        query: GET_NAVBAR_INFO,
      },
    ],
    onError: (error) => handleBookingError(error.graphQLErrors),
    onCompleted: () => handleBookingSuccess(),
  });

  return (
    <LargeModal
      title={t("MY GROUP CLASS")}
      centered
      visible={visible}
      footer={null}
      onCancel={onCloseModal}
    >
      <ConfirmationText>
        {t("Please confirm that you want to book this small group class.")}
      </ConfirmationText>
      <BookingActions>
        <ModalButtonYellow
          type="primary"
          loading={submitting}
          disabled={credits < classCredit}
          onClick={async () => {
            setSubmitting(true);
            try {
              if (await confirmLevel({ lessonId: state.classId })) {
                await bookGroup({ variables: { lessonId: state.classId } });
                onCloseModal();
              }
            } catch (error) {
              handleBookingError([
                {
                  message: t(
                    "Unable to complete your booking. Please try again."
                  ),
                },
              ]);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {t("CONFIRM")}
        </ModalButtonYellow>
        <ModalButtonBlue
          type="primary"
          onClick={() => {
            onCloseModal();
          }}
        >
          {t("CANCEL")}
        </ModalButtonBlue>
      </BookingActions>
      <LargeModalCreditInfo>
        <span>
          {t(
            "This small group class costs {{ classCredit }} credit. You currently have {{ credits }} credit left.",
            {
              credits,
              count: credits,
              classCredit,
            }
          )}
        </span>
        {credits < classCredit ? (
          <CreditWarning>
            {t("You have insufficient credits.")}{" "}
            <Link to="/my-credits" style={{ textDecoration: "underline" }}>
              {t("Buy now")} &gt;
            </Link>
          </CreditWarning>
        ) : null}
        <span>
          {t(
            "You can cancel up to {{ hour }} hours before this class starts.",
            { hour: 24 }
          )}
        </span>
      </LargeModalCreditInfo>
    </LargeModal>
  );
};

export default GroupModal;
